(() => {
  const state = {
    activeSlide: 0,
    lastScrollY: 0,
    rafId: 0,
    heroVisualProgress: 0,
    heroTargetProgress: 0,
    heroCopyVisualProgress: 0,
    heroCopyTargetProgress: 0,
    heroCardVisualProgress: 0,
    heroCardTargetProgress: 0,
  };

  const selectAll = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothStep = (value, start = 0, end = 1) => {
    const t = clamp((value - start) / Math.max(end - start, 0.001));
    return t * t * (3 - 2 * t);
  };
  const getTop = (node) => node.getBoundingClientRect().top + window.scrollY;
  const getBottom = (node) => getTop(node) + node.offsetHeight;

  const syncHeaderMode = (scrollY) => {
    const hero = document.querySelector('[data-hero-root]');
    if (!hero) return;

    const threshold = Math.min(window.innerHeight * 0.2, 180);
    const hasExitedHero = getBottom(hero) - scrollY <= threshold;
    document.documentElement.dataset.headerMode = hasExitedHero ? 'solid' : 'overlay';
  };

  const syncFooterReveal = (scrollY) => {
    const footer = document.querySelector('[data-footer-reveal-root]');
    if (!footer) return;

    const footerTop = getTop(footer);
    const visible = clamp(window.innerHeight - (footerTop - scrollY), 0, footer.offsetHeight);
    const progress = clamp(visible / Math.max(footer.offsetHeight, 1));
    const baseProgress = clamp((progress - 0.02) / 0.3);
    const contentProgress = clamp((progress - 0.34) / 0.46);
    document.documentElement.style.setProperty('--fizo-footer-progress', progress.toFixed(3));
    document.documentElement.style.setProperty('--fizo-footer-base-progress', baseProgress.toFixed(3));
    document.documentElement.style.setProperty('--fizo-footer-content-progress', contentProgress.toFixed(3));
  };

  const syncFloatingCardVisibility = (scrollY) => {
    const start = document.querySelector('[data-floating-card-start]');
    const stop = document.querySelector('[data-floating-card-stop]');
    if (!start) {
      window.FizoFloatingCard?.hide();
      return;
    }
    const header = document.querySelector('[data-header-shell]');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    const anchorY = scrollY + headerBottom;
    const revealOffset = start ? Math.min(window.innerHeight * 0.16, 120) : 0;

    const pastStart = anchorY >= getTop(start) + revealOffset;
    const beforeStop = !stop || anchorY < getTop(stop);

    const shouldShow = pastStart && beforeStop;
    if (shouldShow) window.FizoFloatingCard?.show();
    else window.FizoFloatingCard?.hide();
  };

  const syncHeroSlide = () => {
    const hero = document.querySelector('[data-hero-root]');
    if (!hero) return;

    const slides = selectAll('[data-hero-slide]', hero);
    slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === state.activeSlide);
    });

    const totalNode = hero.querySelector('[data-hero-total]');
    const currentNode = hero.querySelector('[data-hero-current]');
    if (totalNode) totalNode.textContent = String(slides.length).padStart(2, '0');
    if (currentNode) currentNode.textContent = String(state.activeSlide + 1).padStart(2, '0');

    slides.forEach((slide, index) => {
      if (index !== state.activeSlide) {
        slide.style.removeProperty('--fizo-hero-progress');
        slide.style.removeProperty('--fizo-hero-copy-progress');
        slide.style.removeProperty('--fizo-hero-card-progress');
        slide.style.removeProperty('--fizo-hero-scroll-progress');
      }
    });
  };

  const syncHeroVerticalState = (scrollY) => {
    const hero = document.querySelector('[data-hero-root]');
    if (!hero) return false;

    const stage = hero.querySelector('[data-hero-stage]');
    const activeSlide = hero.querySelector('[data-hero-slide].is-active');
    if (!stage || !activeSlide) return false;

    const heroTop = getTop(hero);
    const heroTravel = Math.max(hero.offsetHeight - window.innerHeight, 1);
    const heroProgress = clamp((scrollY - heroTop) / heroTravel);
    const animationStart = 0.01;
    const animationEnd = 0.92;
    const rawProgress = clamp((heroProgress - animationStart) / Math.max(animationEnd - animationStart, 0.01));
    const easedTargetProgress = 1 - Math.pow(1 - rawProgress, 2);
    const copyTargetProgress = smoothStep(rawProgress, 0, 0.82);
    const cardTargetProgress = smoothStep(rawProgress, 0.12, 1);

    state.heroTargetProgress = easedTargetProgress;
    state.heroCopyTargetProgress = copyTargetProgress;
    state.heroCardTargetProgress = cardTargetProgress;

    const motionDelta = state.heroTargetProgress - state.heroVisualProgress;
    const copyDelta = state.heroCopyTargetProgress - state.heroCopyVisualProgress;
    const cardDelta = state.heroCardTargetProgress - state.heroCardVisualProgress;

    if (rawProgress >= 0.985) {
      state.heroVisualProgress = state.heroTargetProgress;
      state.heroCopyVisualProgress = state.heroCopyTargetProgress;
      state.heroCardVisualProgress = state.heroCardTargetProgress;
    } else {
      if (Math.abs(motionDelta) <= 0.002) state.heroVisualProgress = state.heroTargetProgress;
      else state.heroVisualProgress += motionDelta * 0.24;

      if (Math.abs(copyDelta) <= 0.002) state.heroCopyVisualProgress = state.heroCopyTargetProgress;
      else state.heroCopyVisualProgress += copyDelta * 0.26;

      if (Math.abs(cardDelta) <= 0.002) state.heroCardVisualProgress = state.heroCardTargetProgress;
      else state.heroCardVisualProgress += cardDelta * 0.22;
    }

    activeSlide.style.setProperty('--fizo-hero-progress', state.heroVisualProgress.toFixed(3));
    activeSlide.style.setProperty('--fizo-hero-copy-progress', state.heroCopyVisualProgress.toFixed(3));
    activeSlide.style.setProperty('--fizo-hero-card-progress', state.heroCardVisualProgress.toFixed(3));
    activeSlide.style.setProperty('--fizo-hero-scroll-progress', heroProgress.toFixed(3));
    activeSlide.dataset.heroVerticalState = rawProgress >= 1 ? 'b' : 'a';

    return (
      Math.abs(state.heroTargetProgress - state.heroVisualProgress) > 0.002 ||
      Math.abs(state.heroCopyTargetProgress - state.heroCopyVisualProgress) > 0.002 ||
      Math.abs(state.heroCardTargetProgress - state.heroCardVisualProgress) > 0.002
    );
  };

  const render = () => {
    state.rafId = 0;
    const scrollY = state.lastScrollY;

    syncHeaderMode(scrollY);
    const heroAnimating = syncHeroVerticalState(scrollY);
    syncFloatingCardVisibility(scrollY);
    syncFooterReveal(scrollY);

    if (heroAnimating) requestRender();
  };

  const requestRender = () => {
    if (state.rafId) return;
    state.rafId = window.requestAnimationFrame(render);
  };

  const bindHeroControls = () => {
    const hero = document.querySelector('[data-hero-root]');
    if (!hero) return;

    const slides = selectAll('[data-hero-slide]', hero);
    const prev = hero.querySelector('[data-hero-prev]');
    const next = hero.querySelector('[data-hero-next]');

    if (prev) {
      prev.addEventListener('click', () => {
        state.activeSlide = (state.activeSlide - 1 + slides.length) % slides.length;
        syncHeroSlide();
        requestRender();
      });
    }

    if (next) {
      next.addEventListener('click', () => {
        state.activeSlide = (state.activeSlide + 1) % slides.length;
        syncHeroSlide();
        requestRender();
      });
    }
  };

  const onViewportChange = () => {
    state.lastScrollY = window.scrollY || window.pageYOffset || 0;
    requestRender();
  };

  const init = () => {
    state.lastScrollY = window.scrollY || window.pageYOffset || 0;

    syncHeroSlide();
    bindHeroControls();
    requestRender();

    window.addEventListener('scroll', onViewportChange, {passive: true});
    window.addEventListener('resize', onViewportChange);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {once: true});
  } else {
    init();
  }
})();
